import { defineNuxtLink } from 'nuxt/app'
import { h, watch } from 'vue'
import { logger } from '~/utility/logger'
import { Component, Prop, Vue, toNative } from '~/utility/pu-class-decorator'
import { localePath } from '../../../utility/route'
import { excludeQueryParams } from '../../url/query-excluder/excludeQueryParams'
import { GenericLinkData } from './GenericLinkData'

// Create NuxtLink component to work around Nuxt 3.19 dev mode auto-import issue
const NuxtLink = defineNuxtLink({ componentName: 'NuxtLink' })

// Add type declaration for $puLocalePath
declare module '@vue/runtime-core' {
  interface ComponentCustomProperties {
    $puLocalePath: (data: any) => string
  }
}

interface GenericLinkDataNonempty {
  hash?: string
  query: Record<string, any>
  params?: Record<string, any>
  path: string
  name: string
}

@Component({
  emits: ['url-generated'],
})
class GenericLink extends Vue {
  @Prop({ required: false })
  name?: string

  @Prop({ required: false })
  params?: Record<string, any>

  @Prop({ required: false })
  query?: Record<string, any>

  @Prop()
  excludeType?: string

  @Prop({ required: false, default: false })
  disabled: boolean

  @Prop({ default: undefined })
  prefetch: boolean | undefined

  @Prop()
  emitEvents?: boolean

  @Prop({ default: false })
  debugBreadcrumbs?: boolean

  get linkData(): GenericLinkData {
    return {}
  }

  get cleanedLinkData() {
    const linkDataResolved = this.resolveLinkData(this.linkData)
    const cleaned = this.cleanEmptyQueryElements(linkDataResolved)

    return cleaned
  }

  /**
   * Resolved URL for path-based links. Used by render() and the url-generated
   * watcher. Returns null when the link uses a named route or is disabled.
   */
  get resolvedUrl(): string | null {
    const isLoaded =
      !!this.linkData.query || !!this.linkData.path || !!this.linkData.name
    if (this.disabled || this.name || !isLoaded) return null
    const locale = (this.$route.params.lang as string) || 'en-gb'
    return localePath(this.cleanedLinkData, locale, this.$router)
  }

  private stopUrlWatcher?: () => void

  created() {
    if (!this.emitEvents) return
    // Emit url-generated via a watcher instead of inside render() to avoid
    // triggering reactive updates during the render phase, which can cause
    // infinite render loops in production (Vue's recursion guard is dev-only).
    this.stopUrlWatcher = watch(
      () => this.resolvedUrl,
      (url) => {
        if (url) {
          this.$emit('url-generated', url)
        }
      },
      { immediate: true },
    )
  }

  beforeUnmount() {
    this.stopUrlWatcher?.()
  }

  render() {
    if (this.disabled) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call -- TODO: Type $slots.default properly
      return h('span', this.$attrs, this.$slots.default?.())
    }
    if (this.name) {
      const locale = this.$route.params.lang || ''
      const params = {
        lang: locale,
        ...(this.params || {}),
      }

      return h(
        NuxtLink,
        {
          to: {
            name: this.name,
            params,
            query: this.query,
          },
          trailingSlash: 'append',
          prefetch: this.prefetch,
          ...this.$attrs,
        },
        this.$slots.default,
      )
    }
    const url = this.resolvedUrl
    if (url) {
      return h(
        NuxtLink,
        {
          to: url,
          trailingSlash: 'append',
          prefetch: this.prefetch,
          ...this.$attrs,
        },
        this.$slots.default,
      )
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call -- TODO: Type $slots.default properly
    return h('span', this.$attrs, this.$slots.default?.())
  }

  private cleanEmptyQueryElements(
    linkDataResolved: GenericLinkDataNonempty,
  ): GenericLinkDataNonempty {
    const cleaned: Record<string, any> = {}
    for (const [key, value] of Object.entries(linkDataResolved.query)) {
      if (value) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: Type query params as Record<string, string | string[]>
        cleaned[key] = value
      }
    }
    return {
      ...linkDataResolved,
      query: cleaned,
    }
  }

  private resolveLinkData(linkData: GenericLinkData): GenericLinkDataNonempty {
    const linkDataNonempty = ensureLinkDataIsNotEmpty(linkData)
    const queryExcluded = excludeQueryParams({
      query: linkDataNonempty.query,
      excludeType: this.excludeType,
      forceQueryParams: linkData.forceQueryParams,
      excludeQueryParams: linkData.excludeQueryParams,
    })

    if (this.debugBreadcrumbs) {
      logger('🔍 PULINK_LOG - GenericLink resolveLinkData:', {
        linkData,
        linkDataNonempty,
        queryExcluded,
        excludeType: this.excludeType,
      })
    }

    return {
      ...linkDataNonempty,
      query: queryExcluded,
    }
  }
}
export default toNative(GenericLink)

function ensureLinkDataIsNotEmpty(
  linkData: GenericLinkData,
): GenericLinkDataNonempty {
  const hash = linkData.hash
    ? linkData.hash.startsWith('#')
      ? linkData.hash
      : `#${linkData.hash}`
    : undefined

  return {
    hash,
    name: linkData.name || '',
    path: linkData.path || '',
    query: linkData.query || {},
    params: linkData.params || {},
  }
}
